"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  Panel,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  DEFAULT_ASSUMPTIONS,
  DEFAULT_READING_SPEED,
  DEFAULT_SLOW_FACTOR,
  estimateLength,
  minutesAndSeconds,
  QUESTION_TYPES,
  type QuestionType,
} from "@/lib/tool-kit/research-ops/survey-length";

/** English strings. A translated content file overrides these through `ui`. */
export const SURVEY_LENGTH_ESTIMATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the counts",
  questionsHeading: "Your questions",
  questionsHint:
    "How many of each kind does the survey have? Leave blank for none.",
  typeLabels: {
    yesNo: "Yes / No",
    single: "Single choice (one option from a list)",
    multi: "Multiple select (check all that apply)",
    likert: "Single rating or Likert item",
    grid: "Grid or matrix rows (count each row)",
    number: "Number or slider",
    ranking: "Ranking (count each ranked list)",
    shortText: "Short text (a word or a line)",
    openText: "Open-ended (a sentence or more)",
  },
  readingHeading: "Reading and extras",
  introLabel: "Words in the consent, instructions and thank-you text",
  introHint:
    "Count the text a respondent reads once, such as the information sheet and consent.",
  speedLabel: "Silent reading speed (words per minute)",
  speedHint:
    "238 is the mean for silent reading of English non-fiction in Brysbaert's (2019) meta-analysis. Survey respondents often skim, so you may set it higher.",
  slowLabel: "Slower-respondent multiplier",
  slowHint: "Shows how long someone who takes longer than typical would need.",
  adjustSummary: "Adjust the timing for each question type",
  adjustHint:
    "Seconds to decide and respond, not counting reading; and the words a respondent reads per question (the question and its options). These are planning defaults, not measurements: time a few people on a pilot and replace them with what you see.",
  secondsLabel: "{type}: seconds to answer",
  wordsLabel: "{type}: words to read",
  resultsHeading: "Estimated completion time",
  prompt: "Enter your question counts to see the estimate.",
  statTypical: "Typical respondent",
  statSlow: "Slower respondent",
  statAnnounce: "Announce as",
  announce: "about {minutes}",
  minutesUnit: "one: minute|other: minutes",
  statQuestions: "Questions",
  colType: "Question type",
  colCount: "Questions",
  colRead: "Reading",
  colAnswer: "Answering",
  colTotal: "Total",
  introRow: "Consent and instructions",
  totalRow: "Total",
  biggest: "Most of the time is in {type}: {share} of the estimate.",
  contextTypical:
    "For context, in a study of an opt-in online panel in Mexico (Revilla & Ochoa, 2017), the median ideal length was 10 minutes and the median maximum was 20. Panels, topics and incentives differ, and this is one study — not a rule.",
  copySummary: "Copy summary",
  copied: "Copied!",
  summary:
    "The survey has {questions} questions and is estimated to take about {typical} (minutes:seconds) for a typical respondent and {slow} for a slower one, assuming silent reading at {speed} words per minute.",
  example: {
    yesNo: "3",
    single: "10",
    multi: "2",
    likert: "0",
    grid: "20",
    number: "3",
    ranking: "1",
    shortText: "2",
    openText: "2",
    intro: "300",
  },
} as const;

type Counts = Record<QuestionType, string>;
type Assumptions = Record<QuestionType, { seconds: string; words: string }>;

const BLANK_COUNTS = Object.fromEntries(
  QUESTION_TYPES.map((type) => [type, ""])
) as Counts;

const DEFAULT_TEXT = Object.fromEntries(
  QUESTION_TYPES.map((type) => [
    type,
    {
      seconds: String(DEFAULT_ASSUMPTIONS[type].seconds),
      words: String(DEFAULT_ASSUMPTIONS[type].words),
    },
  ])
) as Assumptions;

function numberOr(text: string, fallback: number): number {
  return parseNumberInput(text) ?? fallback;
}

export default function SurveyLengthEstimatorWidget() {
  const strings = useToolUi(SURVEY_LENGTH_ESTIMATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [counts, setCounts] = useState<Counts>(BLANK_COUNTS);
  const [introText, setIntroText] = useState("");
  const [speedText, setSpeedText] = useState(String(DEFAULT_READING_SPEED));
  const [slowText, setSlowText] = useState(String(DEFAULT_SLOW_FACTOR));
  const [assumed, setAssumed] = useState<Assumptions>(DEFAULT_TEXT);

  const result = useMemo(() => {
    const parsedCounts = {} as Record<QuestionType, number>;
    const assumptions = {} as Record<
      QuestionType,
      { seconds: number; words: number }
    >;
    for (const type of QUESTION_TYPES) {
      parsedCounts[type] = Math.max(0, parseIntegerInput(counts[type]) ?? 0);
      assumptions[type] = {
        seconds: Math.max(
          0,
          numberOr(assumed[type].seconds, DEFAULT_ASSUMPTIONS[type].seconds)
        ),
        words: Math.max(
          0,
          numberOr(assumed[type].words, DEFAULT_ASSUMPTIONS[type].words)
        ),
      };
    }
    return estimateLength({
      counts: parsedCounts,
      assumptions,
      introWords: Math.max(0, parseIntegerInput(introText) ?? 0),
      readingSpeed: Math.max(50, numberOr(speedText, DEFAULT_READING_SPEED)),
      slowFactor: Math.max(1, numberOr(slowText, DEFAULT_SLOW_FACTOR)),
    });
  }, [counts, assumed, introText, speedText, slowText]);

  const biggest = [...result.rows].sort(
    (a, b) => b.totalSeconds - a.totalSeconds
  )[0];
  const speed = Math.max(50, numberOr(speedText, DEFAULT_READING_SPEED));
  const hasQuestions = result.questionCount > 0;
  const summary = t(strings.summary, {
    questions: fmt(result.questionCount),
    typical: minutesAndSeconds(result.totalSeconds),
    slow: minutesAndSeconds(result.slowSeconds),
    speed: fmt(speed),
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            const sample = strings.example;
            setCounts(
              Object.fromEntries(
                QUESTION_TYPES.map((type) => [type, sample[type]])
              ) as Counts
            );
            setIntroText(sample.intro);
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setCounts(BLANK_COUNTS);
            setIntroText("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.questionsHeading}>
        <p className={HINT}>{strings.questionsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {QUESTION_TYPES.map((type) => (
            <TextField
              id={`sl-${type}`}
              key={type}
              label={strings.typeLabels[type]}
              numeric
              onChange={(value) =>
                setCounts((previous) => ({ ...previous, [type]: value }))
              }
              value={counts[type]}
            />
          ))}
        </div>
      </Panel>

      <Panel title={strings.readingHeading}>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            hint={strings.introHint}
            id="sl-intro"
            label={strings.introLabel}
            numeric
            onChange={setIntroText}
            value={introText}
          />
          <TextField
            hint={strings.speedHint}
            id="sl-speed"
            label={strings.speedLabel}
            numeric
            onChange={setSpeedText}
            value={speedText}
          />
          <TextField
            hint={strings.slowHint}
            id="sl-slow"
            label={strings.slowLabel}
            numeric
            onChange={setSlowText}
            value={slowText}
          />
        </div>
        <details className="space-y-3">
          <summary className={`${LABEL} cursor-pointer`}>
            {strings.adjustSummary}
          </summary>
          <p className={HINT}>{strings.adjustHint}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {QUESTION_TYPES.flatMap((type) => [
              <TextField
                id={`sl-${type}-seconds`}
                key={`${type}-seconds`}
                label={t(strings.secondsLabel, {
                  type: strings.typeLabels[type],
                })}
                numeric
                onChange={(value) =>
                  setAssumed((previous) => ({
                    ...previous,
                    [type]: { ...previous[type], seconds: value },
                  }))
                }
                value={assumed[type].seconds}
              />,
              <TextField
                id={`sl-${type}-words`}
                key={`${type}-words`}
                label={t(strings.wordsLabel, {
                  type: strings.typeLabels[type],
                })}
                numeric
                onChange={(value) =>
                  setAssumed((previous) => ({
                    ...previous,
                    [type]: { ...previous[type], words: value },
                  }))
                }
                value={assumed[type].words}
              />,
            ])}
          </div>
        </details>
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {hasQuestions ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.statTypical}
                value={minutesAndSeconds(result.totalSeconds)}
              />
              <StatCard
                label={strings.statSlow}
                value={minutesAndSeconds(result.slowSeconds)}
              />
              <StatCard
                label={strings.statAnnounce}
                value={t(strings.announce, {
                  minutes: `${fmt(result.announceMinutes)}${tPlural(strings.minutesUnit, locale, result.announceMinutes)}`,
                })}
              />
            </StatGrid>
            <DataTable
              head={[
                strings.colType,
                strings.colCount,
                strings.colRead,
                strings.colAnswer,
                strings.colTotal,
              ]}
              rows={[
                ...result.rows
                  .filter((row) => row.count > 0)
                  .map((row) => [
                    strings.typeLabels[row.type],
                    fmt(row.count),
                    minutesAndSeconds(row.readSeconds),
                    minutesAndSeconds(row.answerSeconds),
                    minutesAndSeconds(row.totalSeconds),
                  ]),
                ...(result.introSeconds > 0
                  ? [
                      [
                        strings.introRow,
                        "—",
                        minutesAndSeconds(result.introSeconds),
                        "—",
                        minutesAndSeconds(result.introSeconds),
                      ],
                    ]
                  : []),
                [
                  strings.totalRow,
                  fmt(result.questionCount),
                  "",
                  "",
                  minutesAndSeconds(result.totalSeconds),
                ],
              ]}
            />
            {biggest && biggest.totalSeconds > 0 ? (
              <p className="text-foreground text-sm">
                {t(strings.biggest, {
                  type: strings.typeLabels[biggest.type],
                  share: fmt(biggest.totalSeconds / result.totalSeconds, {
                    style: "percent",
                    maximumFractionDigits: 0,
                  }),
                })}
              </p>
            ) : null}
            <div className={`${RESULT_CARD} space-y-3`}>
              <p className="text-foreground text-sm">{summary}</p>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copySummary}
                text={summary}
              />
            </div>
            <p className={HINT}>{strings.contextTypical}</p>
          </>
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
      </section>
    </div>
  );
}
